import React, { useEffect, useMemo, useState } from 'react';
import { grievancesAPI } from '../../services/api';
import { toast } from 'react-hot-toast';

interface GrievanceItem {
  id: number;
  status: 'pending' | 'in-progress' | 'resolved' | 'rejected' | string;
  priority?: string;
  createdAt?: string;
}

const OfficeReports: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [items, setItems] = useState<GrievanceItem[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await grievancesAPI.getAll();
        const data = (res as any).data || [];
        setItems(Array.isArray(data) ? data : []);
      } catch (e: any) {
        toast.error(e?.response?.data?.message || 'Failed to load reports');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const counts = useMemo(() => {
    const init = { pending: 0, inProgress: 0, resolved: 0, rejected: 0 };
    for (const g of items) {
      const s = String(g.status || '').toLowerCase();
      if (s === 'pending') init.pending += 1;
      else if (s === 'in-progress') init.inProgress += 1;
      else if (s === 'resolved') init.resolved += 1;
      else if (s === 'rejected') init.rejected += 1;
    }
    return init;
  }, [items]);

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold">Office Reports & Data</h1>

      {loading ? (
        <div className="min-h-[160px] grid place-items-center">
          <div className="loading-spinner-xl"></div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <ReportCard title="Pending" value={counts.pending} color="bg-yellow-100 text-yellow-800" />
            <ReportCard title="In-Progress" value={counts.inProgress} color="bg-blue-100 text-blue-800" />
            <ReportCard title="Resolved" value={counts.resolved} color="bg-green-100 text-green-800" />
            <ReportCard title="Rejected" value={counts.rejected} color="bg-gray-100 text-gray-800" />
          </div>

          <div className="bg-white rounded-md border p-4">
            <h2 className="text-lg font-semibold mb-3">Recent Grievances</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {items.slice(0, 10).map((g) => (
                    <tr key={g.id}>
                      <td className="px-4 py-2 text-sm text-gray-900">{g.id}</td>
                      <td className="px-4 py-2 text-sm text-gray-700">{g.status}</td>
                      <td className="px-4 py-2 text-sm text-gray-700">{g.createdAt ? new Date(g.createdAt).toLocaleString() : '-'}</td>
                    </tr>
                  ))}
                  {items.length === 0 && (
                    <tr>
                      <td className="px-4 py-4 text-sm text-gray-500" colSpan={3}>No data</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const ReportCard: React.FC<{ title: string; value: number; color: string }> = ({ title, value, color }) => (
  <div className={`rounded-md border p-4 ${color}`}>
    <div className="text-sm font-medium">{title}</div>
    <div className="text-2xl font-bold mt-1">{value}</div>
  </div>
);

export default OfficeReports;
